import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { 
  PlusCircleIcon, 
  BeakerIcon, 
  BoltIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

function AddCookie() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    cookieString: '',
    amount: '',
    maxUses: '30',
    notes: '',
    tags: '',
    randomAmount: false,
    minAmount: '1',
    maxAmount: '15',
    randomUses: false,
    minUses: '1',
    maxUsesRandom: '30'
  });
  const [parsedData, setParsedData] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const addCookieMutation = useMutation(
    (data) => api.post('/cookies', data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('cookies');
        queryClient.invalidateQueries('stats');
        
        const cookie = response.data.cookie;
        let amountText = '';
        
        if (cookie.randomAmount) {
          amountText = `🎲 Monto aleatorio: $${cookie.minAmount} - $${cookie.maxAmount}`;
        } else {
          amountText = `💰 Monto fijo: $${cookie.amount}`;
        }
        
        const usesText = cookie.randomUses ? 
          `🎲 Usos aleatorios: ${cookie.minUses}-${cookie.maxUsesRandom}` : 
          `📊 Usos fijos: ${cookie.maxUses}`;
        
        toast.success(
          <div>
            <p className="font-bold">✅ Cookie Amazon agregada!</p>
            <p className="text-sm">🛒 Servicio: Amazon</p>
            <p className="text-sm">{amountText}</p>
            <p className="text-sm">{usesText}</p>
          </div>,
          { duration: 5000 }
        );
        
        navigate('/cookies');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al agregar cookie');
      }
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    
    if (name === 'cookieString') {
      parseCookie(value);
    }
  };

  const parseCookie = (cookieString) => {
    if (!cookieString) {
      setParsedData(null);
      return;
    }

    const cookies = cookieString.split(';').map(c => c.trim());
    const parsed = {
      total: cookies.length,
      metadata: {}
    };

    const domainMatch = cookieString.match(/domain=([^;\s]+)/i);
    if (domainMatch) parsed.metadata.domain = domainMatch[1];

    const expiresMatch = cookieString.match(/expires=([^;\s]+)/i);
    if (expiresMatch) parsed.metadata.expires = expiresMatch[1];

    setParsedData(parsed);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.cookieString) {
      toast.error('La cookie es requerida');
      return;
    }

    const tags = formData.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag);

    const submitData = {
      cookieString: formData.cookieString,
      notes: formData.notes,
      tags,
      randomAmount: formData.randomAmount,
      randomUses: formData.randomUses
    };

    // Configuración de montos
    if (formData.randomAmount) {
      submitData.minAmount = parseFloat(formData.minAmount);
      submitData.maxAmount = parseFloat(formData.maxAmount);
    } else {
      if (!formData.amount) {
        toast.error('El monto es requerido');
        return;
      }
      submitData.amount = parseFloat(formData.amount);
    }

    // Configuración de usos
    if (formData.randomUses) {
      submitData.minUses = parseInt(formData.minUses);
      submitData.maxUsesRandom = parseInt(formData.maxUsesRandom);
    } else {
      submitData.maxUses = parseInt(formData.maxUses);
    }

    addCookieMutation.mutate(submitData);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#000000', 
      padding: '1rem', 
      paddingBottom: '6rem' 
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        background: '#111111',
        borderRadius: '1.5rem',
        border: '1px solid #222222',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #222222',
          background: '#0a0a0a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <PlusCircleIcon style={{ width: '2rem', height: '2rem', color: '#ff9900' }} />
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ff9900' }}>Agregar Cookie Amazon</h2>
          </div>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Pega la cookie de Amazon completa. Será automáticamente marcada como AMAZON
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          {/* Campo de cookie */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', color: '#ffffff', marginBottom: '0.5rem', fontWeight: '500' }}>
              🛒 Cookie Amazon <span style={{ color: '#ff9900' }}>*</span>
            </label>
            <textarea
              name="cookieString"
              value={formData.cookieString}
              onChange={handleChange}
              rows="5"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#1a1a1a',
                border: '1px solid #333333',
                borderRadius: '0.75rem',
                color: '#ffffff',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                resize: 'vertical'
              }}
              placeholder="session-id=123; session-token=abc; ubid-main=456; ..."
              required
            />
            <p style={{ color: '#ff9900', fontSize: '0.75rem', marginTop: '0.5rem' }}>
              🛒 Esta cookie será automáticamente marcada como AMAZON
            </p>
            
            {parsedData && (
              <div style={{
                marginTop: '0.75rem',
                padding: '1rem',
                background: '#1a1a1a',
                borderRadius: '0.75rem',
                border: '1px solid #ff9900'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <BeakerIcon style={{ width: '1rem', height: '1rem', color: '#ff9900' }} />
                  <span style={{ color: '#ff9900', fontSize: '0.875rem', fontWeight: '500' }}>Análisis automático</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <span>📊 {parsedData.total} pares detectados</span>
                  <span>🛒 Servicio: <span style={{ color: '#ff9900' }}>AMAZON</span></span>
                  {parsedData.metadata.domain && (
                    <span>🌐 {parsedData.metadata.domain}</span>
                  )}
                  {parsedData.metadata.expires && (
                    <span>⏰ Expira: {new Date(parsedData.metadata.expires).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Opciones avanzadas */}
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                color: '#ff9900',
                cursor: 'pointer',
                fontSize: '0.875rem',
                marginBottom: '1rem'
              }}
            >
              <BoltIcon style={{ width: '1rem', height: '1rem' }} />
              {showAdvanced ? '▼ Ocultar configuración' : '► Mostrar configuración avanzada'}
            </button>

            {showAdvanced && (
              <div style={{
                background: '#1a1a1a',
                borderRadius: '1rem',
                padding: '1rem',
                border: '1px solid #333333'
              }}>
                {/* Modo aleatorio de montos */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
                    <input
                      type="checkbox"
                      name="randomAmount"
                      checked={formData.randomAmount}
                      onChange={handleChange}
                      style={{ width: '1.25rem', height: '1.25rem', accentColor: '#ff9900' }}
                    />
                    <span style={{ color: '#ffffff' }}>🎲 Modo Monto Aleatorio</span>
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>(Cada pago cobra un monto diferente)</span>
                  </label>

                  {formData.randomAmount ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Monto Mínimo ($)</label>
                        <input
                          type="number"
                          name="minAmount"
                          value={formData.minAmount}
                          onChange={handleChange}
                          step="0.01"
                          min="0"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            background: '#0a0a0a',
                            border: '1px solid #333333',
                            borderRadius: '0.5rem',
                            color: '#ffffff'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Monto Máximo ($)</label>
                        <input
                          type="number"
                          name="maxAmount"
                          value={formData.maxAmount}
                          onChange={handleChange}
                          step="0.01"
                          min="0"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            background: '#0a0a0a',
                            border: '1px solid #333333',
                            borderRadius: '0.5rem',
                            color: '#ffffff'
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label style={{ color: '#ffffff', marginBottom: '0.25rem', display: 'block' }}>Monto Fijo ($)</label>
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: '#0a0a0a',
                          border: '1px solid #333333',
                          borderRadius: '0.75rem',
                          color: '#ffffff'
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Modo aleatorio de usos */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
                    <input
                      type="checkbox"
                      name="randomUses"
                      checked={formData.randomUses}
                      onChange={handleChange}
                      style={{ width: '1.25rem', height: '1.25rem', accentColor: '#ff9900' }}
                    />
                    <span style={{ color: '#ffffff' }}>🎲 Modo Usos Aleatorios</span>
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>(Número de usos aleatorio al crear)</span>
                  </label>

                  {formData.randomUses ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Usos Mínimos</label>
                        <input
                          type="number"
                          name="minUses"
                          value={formData.minUses}
                          onChange={handleChange}
                          min="1"
                          max="30"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            background: '#0a0a0a',
                            border: '1px solid #333333',
                            borderRadius: '0.5rem',
                            color: '#ffffff'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Usos Máximos</label>
                        <input
                          type="number"
                          name="maxUsesRandom"
                          value={formData.maxUsesRandom}
                          onChange={handleChange}
                          min="1"
                          max="30"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            background: '#0a0a0a',
                            border: '1px solid #333333',
                            borderRadius: '0.5rem',
                            color: '#ffffff'
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label style={{ color: '#ffffff', marginBottom: '0.25rem', display: 'block' }}>Usos Máximos</label>
                      <select
                        name="maxUses"
                        value={formData.maxUses}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: '#0a0a0a',
                          border: '1px solid #333333',
                          borderRadius: '0.75rem',
                          color: '#ffffff'
                        }}
                      >
                        {[1,2,3,4,5,6,7,8,9,10,15,20,25,30].map(num => (
                          <option key={num} value={num}>{num} usos</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Notas y Tags */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', color: '#ffffff', marginBottom: '0.5rem' }}>📝 Notas</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="2"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#1a1a1a',
                border: '1px solid #333333',
                borderRadius: '0.75rem',
                color: '#ffffff',
                resize: 'vertical'
              }}
              placeholder="Información adicional sobre esta cookie Amazon..."
            />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', color: '#ffffff', marginBottom: '0.5rem' }}>
              🏷️ Tags <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>(separados por coma)</span>
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#1a1a1a',
                border: '1px solid #333333',
                borderRadius: '0.75rem',
                color: '#ffffff'
              }}
              placeholder="amazon,prime,giftcard"
            />
          </div>

          {/* Botones */}
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={() => navigate('/cookies')}
              style={{
                flex: 1,
                padding: '0.875rem',
                background: '#1a1a1a',
                border: '1px solid #333333',
                borderRadius: '0.75rem',
                color: '#9ca3af',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={addCookieMutation.isLoading}
              style={{
                flex: 1,
                padding: '0.875rem',
                background: 'linear-gradient(135deg, #ff9900, #ff6600)',
                border: 'none',
                borderRadius: '0.75rem',
                color: '#000000',
                fontWeight: 'bold',
                cursor: addCookieMutation.isLoading ? 'not-allowed' : 'pointer',
                opacity: addCookieMutation.isLoading ? 0.7 : 1
              }}
            >
              {addCookieMutation.isLoading ? 'Guardando...' : '🛒 Guardar Cookie Amazon'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddCookie;